import { useContext, useMemo, useState } from "react";
import { Box, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from "@mui/material";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import { useTranslation } from "react-i18next";
import type { MRT_Row } from "material-react-table";
import GenericCrudTable from "@/components/tables/genericCrudTable";
import type { CrudColumnDef } from "@/components/tables/crudColumnDef";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import GenericNotification from "@/components/notification/genericNotification";
import PermissionContext from "@/context/permissionContext";
import { replaceDate } from "@/helper/dateHandling";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";
import {
  useUnblockLoginAccess,
  type AccessFailureLogRecord,
} from "@/components/accessFailureLog/useUnblockLoginAccess";
import { accessFailureLogEntityAdapter } from "@/components/accessFailureLog/accessFailureLogEntityAdapter";

export default function AccessFailureLogTable() {
  const { t } = useTranslation();
  usePageHeading(t("access_failure_log"));
  const { isAllowedTo } = useContext(PermissionContext);
  const canUnblock = isAllowedTo("unblock_login_access_user");
  const [usernameMatch, setUsernameMatch] = useState<"contains" | "similar">("contains");
  const [refreshTable, setRefreshTable] = useState(false);
  const { unblock, pendingUsers, errorModal, notifications } = useUnblockLoginAccess(
    () => setRefreshTable((previous) => !previous)
  );
  const additionalUrlParameter = useMemo(
    () => ({ username_match: usernameMatch }),
    [usernameMatch]
  );

  const columns = useMemo<CrudColumnDef[]>(() => [
    {
      id: "attempt_time",
      accessorKey: "attempt_time",
      header: t("access_failure_log_attempt_time"),
      filterVariant: "date",
      allowDateRangeFiltering: true,
      filterDateRangeFormat: "datetime",
      dateFilterApiParams: {
        exactAsRange: true,
        gte: "attempt_time__gte",
        lte: "attempt_time__lte",
        gt: "attempt_time__gte",
        lt: "attempt_time__lte",
      },
      Cell: ({ cell }) => replaceDate(cell.getValue<string>(), true, true),
    },
    {
      id: "username",
      accessorKey: "username",
      size: 300,
      header: t("access_failure_log_email_address"),
      customColumnFilterId: "username_query",
      setNoFilterParameter: true,
      muiFilterTextFieldProps: usernameMatch === "similar"
        ? { placeholder: t("access_failure_log_full_email_search") }
        : undefined,
    },
    ...["first_name", "last_name", "company_name"].map((field): CrudColumnDef => ({
      id: field,
      accessorKey: field,
      header: t(field),
      enableSorting: false,
      enableColumnFilter: false,
      Cell: ({ cell }) => cell.getValue<string | null>() || "—",
    })),
    {
      id: "locked_out",
      accessorKey: "locked_out",
      header: t("access_failure_log_locked_out_at_attempt"),
      filterVariant: "select",
      filterSelectOptions: [
        { value: "true", label: t("yes") },
        { value: "false", label: t("no") },
      ],
      setNoFilterParameter: true,
      Cell: ({ cell }) => t(cell.getValue<boolean>() ? "yes" : "no"),
    },
  ], [t, usernameMatch]);

  const customRowActions = (row: MRT_Row<AccessFailureLogRecord>) => {
    const record = row.original;
    if (!canUnblock) return null;
    const userIsLockedOut = record.currently_locked_out === true;
    const userIsAccessible = Boolean(record.user_id) && typeof record.currently_locked_out === "boolean";
    const tooltip = !userIsAccessible
      ? t("access_failure_log_user_unavailable")
      : userIsLockedOut
        ? t("unblock_user")
        : t("access_failure_log_user_not_locked_out");

    return (
      <Tooltip title={tooltip} arrow>
        <span>
          <GenericButton
            variant="icon"
            color="iconBase"
            aria-label={t("unblock_user")}
            disabled={!record.user_id || !userIsLockedOut || pendingUsers.has(record.user_id)}
            onClick={() => unblock(record)}
          >
            <LockOpenIcon />
          </GenericButton>
        </span>
      </Tooltip>
    );
  };

  return (
    <>
      <GenericCrudTable
        entity="accessfailurelog"
        entityAdapter={accessFailureLogEntityAdapter}
        columns={columns}
        readOnlyMode
        disallowEdit
        disallowDelete
        hideEditButtonForRows
        hideDeleteButtonForRows
        disableAddItem
        enableRowSelection={false}
        disableGlobalFilter
        showColumnFilterbyDefault
        disablePersistedColumnFilters
        shouldNotImpersonate
        refreshTable={refreshTable}
        resetPaginationOnFilterChange
        additionalUrlParameter={additionalUrlParameter}
        showRowActions={canUnblock}
        customRowActions={canUnblock ? customRowActions : undefined}
        customRowActionColSize={40}
        hasAdditionalFiltersToReset={usernameMatch !== "contains"}
        onResetAllFilters={() => setUsernameMatch("contains")}
        customTopToolBarAction={() => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography id="access-failure-username-match-label" variant="body2">
              {t("access_failure_log_username_match")}:
            </Typography>
            <ToggleButtonGroup
              value={usernameMatch}
              exclusive
              size="small"
              color="primary"
              aria-labelledby="access-failure-username-match-label"
              onChange={(_event, nextMode: "contains" | "similar" | null) => {
                if (nextMode !== null) setUsernameMatch(nextMode);
              }}
            >
              <ToggleButton value="contains">{t("access_failure_log_contains")}</ToggleButton>
              <ToggleButton value="similar">{t("access_failure_log_similar")}</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}
      />
      {errorModal}
      {notifications.successForNotification && (
        <GenericNotification
          message={notifications.notificationMessage}
          variant={notifications.notificationVariant}
          handleCloseSnackbar={notifications.resetStateOnCloseNotification}
        />
      )}
    </>
  );
}
